import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/adminlayout';
import { PRODUCTION_URL } from '../productionLink/productionLink';
import './guest-refund.css';

const GuestRefund = () => {
    const { bookingId } = useParams();
    const navigate = useNavigate();
    const [eligibility, setEligibility] = useState(null);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [formData, setFormData] = useState({
        refundType: 'partial',
        refundReason: '',
        refundAmount: '',
        deductionAmount: 0,
        deductionReason: '',
        refundMethod: 'cash'
    });

    const refundMethods = [
        { value: 'cash', label: 'Cash' },
        { value: 'card', label: 'Card Refund' },
        { value: 'upi', label: 'UPI Transfer' },
        { value: 'bank_transfer', label: 'Bank Transfer' },
        { value: 'original_payment', label: 'Original Payment Method' }
    ];

    const refundTypes = [
        { value: 'full', label: 'Full Refund' },
        { value: 'partial', label: 'Partial Refund' },
        { value: 'cancellation', label: 'Cancellation Refund' },
        { value: 'adjustment', label: 'Adjustment' }
    ];

    const fetchEligibility = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${PRODUCTION_URL}/api/admin/booking/${bookingId}/refund-eligibility`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const data = await response.json();
            if (data.success) {
                setEligibility(data.data);
                if (data.data.maxRefundable > 0) {
                    setFormData(prev => ({
                        ...prev,
                        refundAmount: data.data.maxRefundable
                    }));
                }
            }
        } catch (err) {
            console.error('Error fetching eligibility:', err);
        } finally {
            setLoading(false);
        }
    }, [bookingId]);

    useEffect(() => {
        if (bookingId) {
            fetchEligibility();
        }
    }, [bookingId, fetchEligibility]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (parseFloat(formData.refundAmount) > eligibility.maxRefundable) {
            alert(`Refund amount cannot exceed ${formatCurrency(eligibility.maxRefundable)}`);
            return;
        }

        if (!window.confirm(`Are you sure you want to process a refund of ${formatCurrency(formData.refundAmount)}?`)) {
            return;
        }

        try {
            setProcessing(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`${PRODUCTION_URL}/api/admin/refund`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    bookingId: parseInt(bookingId),
                    refundType: formData.refundType,
                    refundReason: formData.refundReason,
                    originalAmount: eligibility.totalPaid,
                    refundAmount: parseFloat(formData.refundAmount),
                    deductionAmount: parseFloat(formData.deductionAmount) || 0,
                    deductionReason: formData.deductionReason,
                    refundMethod: formData.refundMethod,
                    processedBy: localStorage.getItem('username') || 'Admin'
                })
            });

            const data = await response.json();
            if (data.success) {
                alert('Refund processed successfully!');
                navigate(`/admin/guest-card/${bookingId}`);
            } else {
                alert(data.message || 'Failed to process refund');
            }
        } catch (err) {
            console.error('Error processing refund:', err);
            alert('Error processing refund');
        } finally {
            setProcessing(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount || 0);
    };

    const calculateFinalRefund = () => {
        const refund = parseFloat(formData.refundAmount) || 0;
        const deduction = parseFloat(formData.deductionAmount) || 0;
        return refund - deduction;
    };

    if (loading) {
        return <div className="refund-container"><div className="loading">Loading...</div></div>;
    }

    return (
        <AdminLayout>
            <div className="refund-container">
                <div className="refund-header">
                    <button onClick={() => navigate(-1)} className="btn-back">← Back</button>
                    <h1>Process Refund</h1>
                </div>

                {eligibility && (
                    <div className="refund-content">
                        {/* Booking Info */}
                        <div className="booking-info-card">
                            <h2>Booking Details</h2>
                            <div className="info-grid">
                                <div>
                                    <label>Guest Name</label>
                                    <span>{eligibility.booking.guest_name}</span>
                                </div>
                                <div>
                                    <label>Room</label>
                                    <span>{eligibility.booking.room_number}</span>
                                </div>
                                <div>
                                    <label>Total Paid</label>
                                    <span className="amount">{formatCurrency(eligibility.totalPaid)}</span>
                                </div>
                                <div>
                                    <label>Already Refunded</label>
                                    <span className="amount refunded">{formatCurrency(eligibility.totalRefunded)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Eligibility Status */}
                        <div className={`eligibility-card ${eligibility.isEligible ? 'eligible' : 'not-eligible'}`}>
                            <div className="eligibility-status">
                                <span className="icon">{eligibility.isEligible ? '✓' : '✗'}</span>
                                <div>
                                    <h3>{eligibility.isEligible ? 'Eligible for Refund' : 'Not Eligible'}</h3>
                                    <p>Maximum refundable: <strong>{formatCurrency(eligibility.maxRefundable)}</strong></p>
                                </div>
                            </div>
                        </div>

                        {/* Refund Form */}
                        {eligibility.isEligible ? (
                            <div className="refund-form-card">
                                <h2>Refund Details</h2>
                                <form onSubmit={handleSubmit}>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Refund Type</label>
                                            <select
                                                name="refundType"
                                                value={formData.refundType}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                {refundTypes.map(type => (
                                                    <option key={type.value} value={type.value}>
                                                        {type.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Refund Method</label>
                                            <select
                                                name="refundMethod"
                                                value={formData.refundMethod}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                {refundMethods.map(method => (
                                                    <option key={method.value} value={method.value}>
                                                        {method.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>Refund Reason</label>
                                        <textarea
                                            name="refundReason"
                                            value={formData.refundReason}
                                            onChange={handleInputChange}
                                            placeholder="Enter reason for refund..."
                                            rows="3"
                                            required
                                        />
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Refund Amount (₹)</label>
                                            <input
                                                type="number"
                                                name="refundAmount"
                                                value={formData.refundAmount}
                                                onChange={handleInputChange}
                                                max={eligibility.maxRefundable}
                                                min="1"
                                                step="0.01"
                                                required
                                            />
                                            <small>Max: {formatCurrency(eligibility.maxRefundable)}</small>
                                        </div>
                                        <div className="form-group">
                                            <label>Deduction Amount (₹)</label>
                                            <input
                                                type="number"
                                                name="deductionAmount"
                                                value={formData.deductionAmount}
                                                onChange={handleInputChange}
                                                min="0"
                                                step="0.01"
                                            />
                                        </div>
                                    </div>

                                    {parseFloat(formData.deductionAmount) > 0 && (
                                        <div className="form-group">
                                            <label>Deduction Reason</label>
                                            <input
                                                type="text"
                                                name="deductionReason"
                                                value={formData.deductionReason}
                                                onChange={handleInputChange}
                                                placeholder="e.g., Cancellation charges, processing fee..."
                                            />
                                        </div>
                                    )}

                                    <div className="refund-summary">
                                        <div className="summary-item">
                                            <span>Refund Amount</span>
                                            <span>{formatCurrency(formData.refundAmount)}</span>
                                        </div>
                                        {parseFloat(formData.deductionAmount) > 0 && (
                                            <div className="summary-item deduction">
                                                <span>Less: Deductions</span>
                                                <span>- {formatCurrency(formData.deductionAmount)}</span>
                                            </div>
                                        )}
                                        <div className="summary-item final">
                                            <span>Final Refund</span>
                                            <span>{formatCurrency(calculateFinalRefund())}</span>
                                        </div>
                                    </div>

                                    <div className="form-actions">
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={() => navigate(-1)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={processing || calculateFinalRefund() <= 0}
                                        >
                                            {processing ? 'Processing...' : 'Process Refund'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        ) : (
                            <div className="not-eligible-message">
                                <p>{eligibility.reason}</p>
                                <button onClick={() => navigate(-1)} className="btn btn-secondary">
                                    Go Back
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

export default GuestRefund;
